import {
  can, daysBetween, parseTodoDate, projectProgress, searchTodos, todoCounts, todoDateLabel, todoTags, todoView, TODO_WAITING_STALE_DAYS, weeklyReview,
  type Todo, type TodoChecklistItem, type TodoPatch, type TodoProject, type TodoView,
} from '@selfon/domain/browser';
import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { api, type ClientRow, type StaffRow, type TodoList } from '../api';
import { useAuth } from '../auth/AuthContext';
import { principalOf } from '../components/Layout';
import { Alert, Chip, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad, ViewDialog } from '../components/ui';
import { dateOnly, thisMonthKst, time, todayKst } from '../format';
import './todos.css';

const VIEW_LABEL: Record<TodoView, string> = { INBOX: '수신함', TODAY: '오늘', UPCOMING: '예정', NEXT: '다음 행동', WAITING: '기다리는 중', SOMEDAY: '언젠가', LOGBOOK: '기록' };
const VIEW_HELP: Record<TodoView, string> = {
  INBOX: '떠오른 일을 일단 여기에 모으고, 하나씩 정리합니다.',
  TODAY: '오늘 하기로 정한 일과 마감이 닥친 일입니다.',
  UPCOMING: '시작일이나 마감일이 앞으로 있는 일입니다.',
  NEXT: '날짜와 상관없이 지금 할 수 있는 일입니다.',
  WAITING: '다른 사람의 답이나 일이 끝나기를 기다립니다.',
  SOMEDAY: '지금은 안 하지만 잊고 싶지 않은 일입니다.',
  LOGBOOK: '끝내거나 취소한 일입니다.',
};
const VIEW_EMPTY: Record<TodoView, string> = {
  INBOX: '수신함이 비었습니다. 머릿속에 남은 일이 있으면 위 칸에 적어 보세요.',
  TODAY: '오늘로 정한 일이 없습니다. 다음 행동에서 골라 보세요.',
  UPCOMING: '앞으로 날짜가 잡힌 일이 없습니다.',
  NEXT: '지금 할 수 있는 일이 없습니다. 수신함을 정리해 보세요.',
  WAITING: '기다리는 일이 없습니다.',
  SOMEDAY: '언젠가 할 일이 없습니다.',
  LOGBOOK: '아직 끝낸 일이 없습니다.',
};

/** crypto.randomUUID 는 보안 연결에서만 있다(단일 HTML 데모는 file://). */
const newId = () => crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

type Place = { kind: 'view'; view: TodoView } | { kind: 'project'; id: string } | { kind: 'review' };
/** 오류는 기본으로 화면 위쪽에 보인다. 대화상자 안에서 부를 때는 onError 로 대화상자 안에 띄운다. */
type Run = (fn: () => Promise<unknown>, onError?: (message: string) => void) => Promise<void>;

/** 메모의 간단한 서식: - 목록, 1. 번호, *기울임*. HTML 로 해석하지 않고 글자 그대로 React 요소로 만든다. */
function NoteText({ text }: { text: string }) {
  const inline = (s: string) => s.split(/(\*[^*\n]+\*)/g).map((part, i) => (/^\*[^*\n]+\*$/.test(part) ? <em key={i}>{part.slice(1, -1)}</em> : part));
  return <div className="todo-note">{text.split('\n').map((line, i) => {
    const m = /^(\s*)(-|\d+\.)\s+(.*)$/.exec(line);
    return m ? <p key={i} className="todo-note-item" style={{ paddingLeft: 8 + m[1]!.length * 8 }}><span aria-hidden="true">{m[2] === '-' ? '–' : m[2]}</span> {inline(m[3]!)}</p> : <p key={i}>{inline(line) }{!line && ' '}</p>;
  })}</div>;
}

function Ring({ ratio, size = 18 }: { ratio: number; size?: number }) {
  const r = size / 2 - 2, len = 2 * Math.PI * r;
  return (
    <svg className="todo-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line)" strokeWidth={2} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--accent)" strokeWidth={2} strokeDasharray={`${ratio * len} ${len}`} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
}

function TodoRow({ todo, projects, today, run, onOpen }: { todo: Todo; projects: TodoProject[]; today: string; run: Run; onOpen: (t: Todo) => void }) {
  const project = projects.find((p) => p.id === todo.projectId);
  const closed = todo.status !== 'OPEN';
  const left = todo.deadline ? daysBetween(today, todo.deadline) : null;
  const checked = todo.checklist.filter((c) => c.done).length;
  return (
    <li className={`todo-row${closed ? ' closed' : ''}`}>
      <input type="checkbox" className="todo-check" checked={closed} aria-label={`${todo.title} ${closed ? '다시 열기' : '완료'}`}
        onChange={() => void run(() => api.setTodoStatus(todo.id, todo.version, closed ? 'OPEN' : 'DONE'))} />
      <button type="button" className="todo-row-main" onClick={() => onOpen(todo)}>
        <span className="todo-title">{todo.title}</span>
        <span className="todo-meta">
          {todo.startDate && todo.startDate > today && <span className="todo-date">{todoDateLabel(todo.startDate, today)}</span>}
          {project && <span>{project.title}</span>}
          {todo.bucket === 'WAITING' && todo.waitingFor && <span>{todo.waitingFor} 기다리는 중</span>}
          {todo.delegatedFrom && <span>{todo.delegatedFrom.name}님이 맡긴 일</span>}
          {todo.checklist.length > 0 && <span>체크리스트 {checked}/{todo.checklist.length}</span>}
          {todo.notes && <span aria-label="메모 있음">메모</span>}
          {todo.status === 'CANCELED' && <span>{todo.canceledByDelegator ? '원장이 취소함' : '취소됨'}</span>}
          {closed && todo.completedAt && <span>{dateOnly(todo.completedAt)}</span>}
        </span>
      </button>
      <span className="todo-right">
        {todo.delegatedTo?.doneAt && todo.status === 'OPEN' && <Chip tone="ok">끝냈다고 알림</Chip>}
        {todo.tags.map((t) => <span key={t} className="todo-tag">{t}</span>)}
        {left !== null && !closed && <span className={`todo-deadline${left < 0 ? ' over' : left <= 1 ? ' soon' : ''}`}>{left < 0 ? `${-left}일 지남` : left === 0 ? '오늘 마감' : `${left}일 남음`}</span>}
      </span>
    </li>
  );
}

function RowList({ todos, ...rest }: { todos: Todo[]; projects: TodoProject[]; today: string; run: Run; onOpen: (t: Todo) => void }) {
  return <ul className="todo-list">{todos.map((t) => <TodoRow key={t.id} todo={t} {...rest} />)}</ul>;
}

function TagFilter({ tags, value, onChange }: { tags: string[]; value: string | null; onChange: (t: string | null) => void }) {
  if (!tags.length) return null;
  return (
    <div className="todo-tags" role="group" aria-label="태그로 거르기">
      <button type="button" className="course-chip" aria-pressed={value === null} onClick={() => onChange(null)}>전체</button>
      {tags.map((t) => <button key={t} type="button" className="course-chip" aria-pressed={value === t} onClick={() => onChange(value === t ? null : t)}>{t}</button>)}
    </div>
  );
}

/** 날짜 입력: 달력으로 고르거나 "내일", "월", "11/20" 처럼 적으면 후보를 보여 준다. */
function DateInput({ id, label, value, today, onChange }: { id: string; label: string; value: string | null; today: string; onChange: (d: string | null) => void }) {
  const [text, setText] = useState('');
  const options = parseTodoDate(text, today);
  return (
    <Field id={id} label={label}>
      <div className="todo-date-input">
        <input id={id} type="date" max="9999-12-31" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} />
        <input type="text" aria-label={`${label} 빠르게 적기`} placeholder="내일, 월, 11/20" maxLength={20} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (options[0]) { onChange(options[0]); setText(''); } } }} />
      </div>
      {options.length > 0 && <div className="todo-date-options">
        {options.map((d) => <button key={d} type="button" className="btn btn-outline btn-sm" onClick={() => { onChange(d); setText(''); }}>{todoDateLabel(d, today)} · {daysBetween(today, d)}일 뒤</button>)}
      </div>}
    </Field>
  );
}

interface Draft {
  title: string; notes: string; checklist: TodoChecklistItem[]; tags: string; bucket: Todo['bucket']; startDate: string | null; evening: boolean;
  deadline: string | null; projectId: string | null; headingId: string | null; waitingFor: string;
}
const draftOf = (t: Todo): Draft => ({
  title: t.title, notes: t.notes, checklist: t.checklist, tags: t.tags.join(', '), bucket: t.bucket, startDate: t.startDate ?? null, evening: t.evening,
  deadline: t.deadline ?? null, projectId: t.projectId ?? null, headingId: t.headingId ?? null, waitingFor: t.waitingFor ?? '',
});
const patchOf = (d: Draft): TodoPatch => ({
  title: d.title, notes: d.notes, checklist: d.checklist, tags: d.tags.split(',').map((x) => x.trim()).filter(Boolean), bucket: d.bucket, startDate: d.startDate, evening: d.evening,
  deadline: d.deadline, projectId: d.projectId, headingId: d.headingId, waitingFor: d.bucket === 'WAITING' ? d.waitingFor : null,
});
type WhenKey = 'INBOX' | 'TODAY' | 'EVENING' | 'ANYTIME' | 'SOMEDAY' | 'WAITING';
const WHEN: Array<[WhenKey, string]> = [['INBOX', '수신함'], ['TODAY', '오늘'], ['EVENING', '오늘 저녁'], ['ANYTIME', '언제든'], ['SOMEDAY', '언젠가'], ['WAITING', '기다리는 중']];
const whenOf = (d: Draft, today: string): WhenKey | null => {
  if (d.bucket !== 'ACTIVE') return d.bucket;
  if (!d.startDate) return 'ANYTIME';
  return d.startDate <= today ? (d.evening ? 'EVENING' : 'TODAY') : null;
};
const applyWhen = (d: Draft, w: WhenKey, today: string): Draft => {
  switch (w) {
    case 'TODAY': return { ...d, bucket: 'ACTIVE', startDate: today, evening: false };
    case 'EVENING': return { ...d, bucket: 'ACTIVE', startDate: today, evening: true };
    case 'ANYTIME': return { ...d, bucket: 'ACTIVE', startDate: null, evening: false };
    default: return { ...d, bucket: w, evening: false };
  }
};

/** 할 일 자세히: 메모·체크리스트·태그·언제·마감·프로젝트. 끝난 일은 다시 열거나 지우기만 한다. */
function TodoEditor({ todo, projects, tags, today, teachers, clients, canDelegate, run, onClose }: {
  todo: Todo | null; projects: TodoProject[]; tags: string[]; today: string; teachers: StaffRow[]; clients: ClientRow[]; canDelegate: boolean; run: Run; onClose: () => void;
}) {
  const [d, setD] = useState<Draft | null>(null);
  const [item, setItem] = useState('');
  const [assignee, setAssignee] = useState('');
  const [clientId, setClientId] = useState('');
  useEffect(() => { setD(todo ? draftOf(todo) : null); setItem(''); setAssignee(''); setClientId(''); }, [todo]);
  // 아동을 연결하면 그 아동의 담당 선생님만 고를 수 있다(서버도 같은 규칙으로 막는다)
  const linked = clients.find((c) => c.id === clientId);
  const assignable = linked ? teachers.filter((s) => linked.therapistIds?.includes(s.userId)) : teachers;
  const set = (p: Partial<Draft>) => setD((x) => (x ? { ...x, ...p } : x));
  const addItem = () => {
    if (!d || !item.trim()) return;
    set({ checklist: [...d.checklist, { id: newId(), text: item.trim(), done: false }] });
    setItem('');
  };
  const finish = (fn: () => Promise<unknown>) => { onClose(); return run(fn); };
  const project = projects.find((p) => p.id === d?.projectId);
  const closed = todo?.status !== 'OPEN';
  const when = d ? whenOf(d, today) : null;

  return (
    <Dialog title={closed ? '끝난 할 일' : '할 일 자세히'} open={Boolean(todo && d)} onClose={onClose} wide hideSubmit={closed} submitLabel="저장"
      onSubmit={async () => { await api.updateTodo(todo!.id, todo!.version, patchOf(d!)); await run(async () => undefined); }}>
      {todo && d && (closed ? (
        <>
          <p><strong>{todo.title}</strong></p>
          {todo.notes && <NoteText text={todo.notes} />}
          <div className="row">
            {!(todo.delegatedTo || (todo.delegatedFrom && todo.status === 'CANCELED')) && <button type="button" className="btn btn-outline" onClick={() => void finish(() => api.setTodoStatus(todo.id, todo.version, 'OPEN'))}>다시 열기</button>}
            <button type="button" className="btn btn-danger" onClick={() => void finish(() => api.deleteTodo(todo.id))}>완전히 지우기</button>
          </div>
        </>
      ) : (
        <>
          {todo.canceledByDelegator && <Alert kind="info">원장이 취소함: 맡긴 분이 이 일을 취소해 내용을 지웠습니다.</Alert>}
          {todo.delegatedFrom && !todo.canceledByDelegator && <Alert kind="info">{todo.delegatedFrom.name}님이 맡긴 일입니다. 끝내면 끝났다는 사실만 전해지고, 여기에 적은 메모는 전해지지 않습니다.</Alert>}
          {todo.delegatedTo && <Alert kind={todo.delegatedTo.doneAt ? 'ok' : 'info'}>{todo.delegatedTo.name}님에게 맡긴 일입니다. {todo.delegatedTo.doneAt ? '끝냈다고 알려 왔습니다. 확인한 뒤 완료하세요.' : '끝나면 여기에 표시됩니다.'}</Alert>}
          <Field id="todo-title" label="제목"><input id="todo-title" required maxLength={200} value={d.title} onChange={(e) => set({ title: e.target.value })} /></Field>
          <Field id="todo-notes" label="메모" hint="- 목록, 1. 번호, *기울임* 을 쓸 수 있습니다."><textarea id="todo-notes" rows={4} maxLength={4000} value={d.notes} onChange={(e) => set({ notes: e.target.value })} /></Field>
          {/[*]|^\s*(-|\d+\.)\s/m.test(d.notes) && <NoteText text={d.notes} />}
          <fieldset className="todo-fieldset">
            <legend>체크리스트</legend>
            <ul className="todo-checklist">
              {d.checklist.map((c) => (
                <li key={c.id}>
                  <input type="checkbox" checked={c.done} aria-label={`${c.text} 완료`} onChange={() => set({ checklist: d.checklist.map((x) => (x.id === c.id ? { ...x, done: !x.done } : x)) })} />
                  <input type="text" aria-label="체크리스트 항목" maxLength={200} value={c.text} onChange={(e) => set({ checklist: d.checklist.map((x) => (x.id === c.id ? { ...x, text: e.target.value } : x)) })} />
                  <button type="button" className="btn btn-ghost btn-sm" aria-label={`${c.text} 빼기`} onClick={() => set({ checklist: d.checklist.filter((x) => x.id !== c.id) })}>빼기</button>
                </li>
              ))}
            </ul>
            <div className="row">
              <input type="text" aria-label="체크리스트 항목 추가" placeholder="항목 추가" maxLength={200} value={item} onChange={(e) => setItem(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }} />
              <button type="button" className="btn btn-outline btn-sm" onClick={addItem}>추가</button>
            </div>
          </fieldset>
          <fieldset className="todo-fieldset">
            <legend>언제 할까요</legend>
            <div className="todo-when" role="group" aria-label="언제 할지 고르기">
              {WHEN.map(([k, label]) => <button key={k} type="button" className="course-chip" disabled={Boolean(todo.delegatedTo)} aria-pressed={when === k} onClick={() => setD(applyWhen(d, k, today))}>{label}</button>)}
            </div>
            {d.bucket === 'WAITING'
              ? <Field id="todo-waiting" label="누구를 기다리나요"><input id="todo-waiting" required maxLength={80} readOnly={Boolean(todo.delegatedTo)} value={d.waitingFor} onChange={(e) => set({ waitingFor: e.target.value })} /></Field>
              : !todo.delegatedTo && <DateInput id="todo-start" label="시작일" value={d.startDate} today={today} onChange={(startDate) => set({ startDate, evening: false, bucket: startDate ? 'ACTIVE' : d.bucket })} />}
          </fieldset>
          <div className="grid-2">
            <DateInput id="todo-deadline" label="마감일" value={d.deadline} today={today} onChange={(deadline) => set({ deadline })} />
            <Field id="todo-tags" label="태그" hint={tags.length ? `쓰는 태그: ${tags.slice(0, 6).join(', ')}` : '쉼표로 나눕니다. 예: 전화, 서류'}><input id="todo-tags" maxLength={200} value={d.tags} onChange={(e) => set({ tags: e.target.value })} /></Field>
            <Field id="todo-project" label="프로젝트">
              <select id="todo-project" value={d.projectId ?? ''} onChange={(e) => set({ projectId: e.target.value || null, headingId: null, bucket: e.target.value && d.bucket === 'INBOX' ? 'ACTIVE' : d.bucket })}>
                <option value="">없음</option>
                {projects.filter((p) => p.status === 'OPEN' || p.id === d.projectId).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </Field>
            {project && project.headings.length > 0 && (
              <Field id="todo-heading" label="제목(구역)">
                <select id="todo-heading" value={d.headingId ?? ''} onChange={(e) => set({ headingId: e.target.value || null })}>
                  <option value="">없음</option>
                  {project.headings.map((h) => <option key={h.id} value={h.id}>{h.title}</option>)}
                </select>
              </Field>
            )}
          </div>
          {canDelegate && !todo.delegatedTo && !todo.delegatedFrom && teachers.length > 0 && (
            <fieldset className="todo-fieldset">
              <legend>선생님에게 맡기기</legend>
              <Alert kind="info">아동 개인정보(이름·진단·연락처 등)를 제목·메모에 적지 마세요. 제목·메모·체크리스트·마감이 선생님 수신함으로 그대로 갑니다.</Alert>
              <div className="row">
                {clients.length > 0 && (
                  <select aria-label="관련 아동(선택)" value={clientId} onChange={(e) => { setClientId(e.target.value); setAssignee(''); }}>
                    <option value="">관련 아동 없음</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
                  </select>
                )}
                <select aria-label="맡길 선생님" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                  <option value="">선생님 고르기</option>
                  {assignable.map((s) => <option key={s.userId} value={s.userId}>{s.name} ({s.title})</option>)}
                </select>
                <button type="button" className="btn btn-outline" disabled={!assignee} onClick={() => void finish(() => api.delegateTodo(todo.id, todo.version, assignee, clientId || undefined))}>맡기기</button>
              </div>
              <p className="hint">관련 아동을 고르면 그 아동의 담당 선생님에게만 맡길 수 있습니다. 맡긴 일을 취소하거나 지우면 선생님 쪽 사본도 지워집니다.</p>
            </fieldset>
          )}
          <div className="row">
            <button type="button" className="btn btn-outline" onClick={() => void finish(() => api.setTodoStatus(todo.id, todo.version, 'DONE'))}>완료</button>
            {!todo.delegatedFrom && <button type="button" className="btn btn-ghost" onClick={() => void finish(() => api.setTodoStatus(todo.id, todo.version, 'CANCELED'))}>취소됨으로 표시</button>}
            {!todo.delegatedFrom && <button type="button" className="btn btn-ghost" onClick={() => void finish(() => api.deleteTodo(todo.id))}>지우기</button>}
          </div>
        </>
      ))}
    </Dialog>
  );
}

/** 명료화: 수신함 항목을 하나씩 질문으로 정리한다(행동 필요? → 2분? → 누가? → 언제?). */
function Clarify({ open, onClose, inbox, projects, teachers, canDelegate, today, run }: {
  open: boolean; onClose: () => void; inbox: Todo[]; projects: TodoProject[]; teachers: StaffRow[]; canDelegate: boolean; today: string; run: Run;
}) {
  const [step, setStep] = useState<'action' | 'two' | 'who' | 'when'>('action');
  const [skipped, setSkipped] = useState<string[]>([]);
  const [waitingFor, setWaitingFor] = useState('');
  const [assignee, setAssignee] = useState('');
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [date, setDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const todo = inbox.find((t) => !skipped.includes(t.id));
  const id = todo?.id;
  useEffect(() => { setStep('action'); setWaitingFor(''); setAssignee(''); setTitle(todo?.title ?? ''); setProjectId(''); setDate(null); setError(null); }, [id, todo?.title]);
  useEffect(() => { if (open) { setSkipped([]); setError(null); } }, [open]);
  // 대화상자가 화면을 덮으므로 실패는 대화상자 안에 보여 준다
  const act = (fn: () => Promise<unknown>) => { setError(null); return run(fn, setError); };
  const place = (patch: TodoPatch) => act(() => api.updateTodo(todo!.id, todo!.version, { title, projectId: projectId || null, ...patch }));
  return (
    <ViewDialog title={todo ? '수신함 정리' : '정리 끝'} open={open} onClose={onClose} className="todo-clarify">
      {!todo ? <Empty>수신함을 모두 정리했습니다.</Empty> : (
        <div className="stack">
          {error && <Alert>{error}</Alert>}
          <p className="muted small">남은 항목 {inbox.length - skipped.length}개</p>
          <p className="todo-clarify-item"><strong>{todo.title}</strong></p>
          {todo.delegatedFrom && <p className="hint">{todo.delegatedFrom.name}님이 맡긴 일입니다.</p>}
          {todo.notes && <NoteText text={todo.notes} />}
          {step === 'action' && <>
            <h3>지금 해야 할 행동이 있나요?</h3>
            <div className="row">
              <button type="button" className="btn btn-primary" onClick={() => setStep('two')}>네, 있어요</button>
              <button type="button" className="btn btn-outline" onClick={() => void place({ bucket: 'SOMEDAY' })}>나중에 볼게요 (언젠가)</button>
              {!todo.delegatedFrom && <button type="button" className="btn btn-ghost" onClick={() => void act(() => api.deleteTodo(todo.id))}>필요 없어요 (지우기)</button>}
              <button type="button" className="btn btn-ghost" onClick={() => setSkipped([...skipped, todo.id])}>건너뛰기</button>
            </div>
          </>}
          {step === 'two' && <>
            <h3>2분 안에 끝낼 수 있나요?</h3>
            <p className="hint">그렇다면 지금 바로 하고 끝내는 편이 정리하는 것보다 빠릅니다.</p>
            <div className="row">
              <button type="button" className="btn btn-primary" onClick={() => void act(() => api.setTodoStatus(todo.id, todo.version, 'DONE'))}>방금 끝냈어요</button>
              <button type="button" className="btn btn-outline" onClick={() => setStep('who')}>더 걸려요</button>
            </div>
          </>}
          {step === 'who' && <>
            <h3>누가 하나요?</h3>
            <div className="row"><button type="button" className="btn btn-primary" onClick={() => setStep('when')}>제가 합니다</button></div>
            {canDelegate && !todo.delegatedFrom && teachers.length > 0 && (
              <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); void act(() => api.delegateTodo(todo.id, todo.version, assignee)); }}>
                <select required aria-label="맡길 선생님" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                  <option value="">선생님 고르기</option>
                  {teachers.map((s) => <option key={s.userId} value={s.userId}>{s.name} ({s.title})</option>)}
                </select>
                <button type="submit" className="btn btn-outline">선생님에게 맡기기</button>
              </form>
            )}
            {canDelegate && !todo.delegatedFrom && teachers.length > 0 && <Alert kind="info">아동 개인정보(이름·진단·연락처 등)를 적지 마세요. 내용이 선생님 수신함으로 그대로 갑니다.</Alert>}
            <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); void place({ bucket: 'WAITING', waitingFor }); }}>
              <input type="text" required maxLength={80} aria-label="기다리는 대상" placeholder="다른 사람이나 기관을 기다려요" value={waitingFor} onChange={(e) => setWaitingFor(e.target.value)} />
              <button type="submit" className="btn btn-outline">기다리는 중으로</button>
            </form>
          </>}
          {step === 'when' && <>
            <h3>구체적인 다음 행동과 때를 정해 주세요</h3>
            <Field id="clarify-title" label="다음 행동"><input id="clarify-title" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
            <Field id="clarify-project" label="프로젝트" hint="한 번에 끝나지 않는 일이면 프로젝트에 넣으세요.">
              <select id="clarify-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">없음</option>
                {projects.filter((p) => p.status === 'OPEN').map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </Field>
            <div className="row">
              <button type="button" className="btn btn-primary" onClick={() => void place({ bucket: 'ACTIVE', startDate: today })}>오늘</button>
              <button type="button" className="btn btn-outline" onClick={() => void place({ bucket: 'ACTIVE', startDate: today, evening: true })}>오늘 저녁</button>
              <button type="button" className="btn btn-outline" onClick={() => void place({ bucket: 'ACTIVE' })}>언제든</button>
            </div>
            <DateInput id="clarify-date" label="다른 날" value={date} today={today} onChange={setDate} />
            {date && <button type="button" className="btn btn-primary" onClick={() => void place({ bucket: 'ACTIVE', startDate: date })}>{todoDateLabel(date, today)}로 정하기</button>}
          </>}
        </div>
      )}
    </ViewDialog>
  );
}

function ProjectDialog({ project, open, onClose, run, onCreated }: { project: TodoProject | null; open: boolean; onClose: () => void; run: Run; onCreated: (id: string) => void }) {
  const [f, setF] = useState({ title: '', goal: '', area: '' });
  const [deadline, setDeadline] = useState<string | null>(null);
  useEffect(() => { if (open) { setF({ title: project?.title ?? '', goal: project?.goal ?? '', area: project?.area ?? '' }); setDeadline(project?.deadline ?? null); } }, [open, project]);
  return (
    <Dialog title={project ? '프로젝트 고치기' : '새 프로젝트'} open={open} onClose={onClose} submitLabel="저장" onSubmit={async () => {
      if (project) await api.updateTodoProject(project.id, project.version, { ...f, deadline });
      else onCreated((await api.createTodoProject({ ...f, deadline: deadline ?? undefined })).project.id);
      await run(async () => undefined);
    }}>
      <Field id="prj-title" label="프로젝트 이름"><input id="prj-title" required maxLength={100} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
      <Field id="prj-goal" label="목표" hint="무엇이 되면 끝난 건가요?"><textarea id="prj-goal" rows={2} maxLength={2000} value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })} /></Field>
      <Field id="prj-area" label="영역" hint="왼쪽 목록에서 묶어 보여 줍니다. 예: 센터 운영, 가족"><input id="prj-area" maxLength={40} value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} /></Field>
      <Field id="prj-deadline" label="마감일"><input id="prj-deadline" type="date" max="9999-12-31" value={deadline ?? ''} onChange={(e) => setDeadline(e.target.value || null)} /></Field>
    </Dialog>
  );
}

function ProjectPage({ project, todos, projects, today, run, onOpen, onLeave }: { project: TodoProject; todos: Todo[]; projects: TodoProject[]; today: string; run: Run; onOpen: (t: Todo) => void; onLeave: () => void }) {
  const [editing, setEditing] = useState(false);
  const [tag, setTag] = useState<string | null>(null);
  const [heading, setHeading] = useState('');
  const progress = projectProgress(project, todos);
  const mine = todos.filter((t) => t.projectId === project.id);
  const open = mine.filter((t) => t.status === 'OPEN' && (!tag || t.tags.includes(tag)));
  const done = mine.filter((t) => t.status !== 'OPEN');
  const known = new Set(project.headings.map((h) => h.id));
  const rest = { projects, today, run, onOpen };
  const addHeading = (e: FormEvent) => {
    e.preventDefault();
    const title = heading.trim();
    if (!title) return;
    setHeading('');
    void run(() => api.updateTodoProject(project.id, project.version, { headings: [...project.headings, { id: newId(), title }] }));
  };
  return (
    <section className="stack" aria-labelledby="todo-project-title">
      <div className="todo-project-head">
        <Ring ratio={progress.ratio} size={36} />
        <div>
          <h2 id="todo-project-title">{project.title}</h2>
          <p className="muted small">{progress.done}/{progress.total} 완료{project.deadline ? ` · ${todoDateLabel(project.deadline, today)} 마감` : ''}{project.status === 'SOMEDAY' ? ' · 쉬는 중' : ''}</p>
        </div>
      </div>
      {project.goal && <p className="todo-project-goal">{project.goal}</p>}
      <div className="row">
        <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>고치기</button>
        {project.status === 'OPEN' && <button type="button" className="btn btn-outline btn-sm" onClick={() => void run(() => api.setTodoProjectStatus(project.id, project.version, 'DONE'))}>프로젝트 완료</button>}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void run(() => api.setTodoProjectStatus(project.id, project.version, project.status === 'OPEN' ? 'SOMEDAY' : 'OPEN'))}>{project.status === 'OPEN' ? '언젠가로 쉬기' : '다시 시작'}</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { if (window.confirm('프로젝트를 지울까요? 안의 할 일은 남습니다.')) { onLeave(); void run(() => api.deleteTodoProject(project.id)); } }}>지우기</button>
      </div>
      <TagFilter tags={todoTags(mine)} value={tag} onChange={setTag} />
      <RowList todos={open.filter((t) => !t.headingId || !known.has(t.headingId))} {...rest} />
      {project.headings.map((h) => (
        <div key={h.id} className="todo-section">
          <h3>{h.title}</h3>
          <RowList todos={open.filter((t) => t.headingId === h.id)} {...rest} />
        </div>
      ))}
      <form className="row" onSubmit={addHeading}>
        <input type="text" aria-label="제목 추가" placeholder="제목으로 구역 나누기" maxLength={60} value={heading} onChange={(e) => setHeading(e.target.value)} />
        <button type="submit" className="btn btn-outline btn-sm">제목 추가</button>
      </form>
      {done.length > 0 && <details className="todo-section"><summary>끝난 일 {done.length}개</summary><RowList todos={done} {...rest} /></details>}
      <ProjectDialog project={project} open={editing} onClose={() => setEditing(false)} run={run} onCreated={() => undefined} />
    </section>
  );
}

/** 주간 검토: 칸마다 확인하고 끝내면 기록을 남긴다. */
function ReviewPage({ data, today, run, onOpen, onClarify, onProject }: { data: TodoList; today: string; run: Run; onOpen: (t: Todo) => void; onClarify: () => void; onProject: (id: string) => void }) {
  const r = weeklyReview(data.todos, data.projects, today, data.lastReviewedAt ?? undefined);
  const [checked, setChecked] = useState<string[]>([]);
  const rest = { projects: data.projects, today, run, onOpen };
  const staleIds = new Set(r.staleWaiting.map((t) => t.id));
  const steps: Array<{ id: string; title: string; help: string; body: ReactNode }> = [
    { id: 'inbox', title: `수신함 비우기 (${r.inbox.length})`, help: '남은 항목을 모두 정리합니다.', body: r.inbox.length > 0 && <button type="button" className="btn btn-outline btn-sm" onClick={onClarify}>하나씩 정리하기</button> },
    { id: 'overdue', title: `마감 지난 일 (${r.overdue.length})`, help: '날짜를 다시 잡거나, 끝내거나, 내려놓습니다.', body: <RowList todos={r.overdue} {...rest} /> },
    { id: 'stalled', title: `다음 행동이 없는 프로젝트 (${r.stalled.length})`, help: '프로젝트마다 다음 행동을 하나 정합니다.', body: <ul className="plain">{r.stalled.map((p) => <li key={p.id}><button type="button" className="btn btn-ghost btn-sm" onClick={() => onProject(p.id)}>{p.title}</button></li>)}</ul> },
    { id: 'waiting', title: `기다리는 중 (${r.waiting.length})`, help: `${TODO_WAITING_STALE_DAYS}일 넘게 소식이 없으면 먼저 연락해 보세요.`, body: <>
      {r.staleWaiting.length > 0 && <p className="small"><Chip tone="warn">오래됨 {staleIds.size}건</Chip></p>}
      <RowList todos={r.waiting} {...rest} />
    </> },
    { id: 'someday', title: `언젠가 훑어보기 (${r.someday.length})`, help: '이제 시작할 일이 있으면 꺼내고, 필요 없는 일은 지웁니다.', body: <RowList todos={r.someday} {...rest} /> },
  ];
  return (
    <section className="stack" aria-labelledby="todo-review-title">
      <div>
        <h2 id="todo-review-title">주간 검토</h2>
        <p className="muted small">{data.lastReviewedAt ? `지난 검토 ${dateOnly(data.lastReviewedAt)}` : '아직 검토한 적이 없습니다'} · 최근 7일에 끝낸 일 {r.doneThisWeek}개</p>
      </div>
      {steps.map((s) => (
        <div key={s.id} className="todo-review-step">
          <label>
            <input type="checkbox" checked={checked.includes(s.id)} onChange={() => setChecked(checked.includes(s.id) ? checked.filter((x) => x !== s.id) : [...checked, s.id])} />
            <span><strong>{s.title}</strong><span className="hint">{s.help}</span></span>
          </label>
          {s.body}
        </div>
      ))}
      <button type="button" className="btn btn-primary" disabled={checked.length < steps.length} onClick={() => { setChecked([]); void run(() => api.markTodoReview()); }}>주간 검토 끝내기</button>
    </section>
  );
}

/** 오늘 위쪽의 일정 요약: 센터 일정을 볼 수 있는 역할만. 실패해도 할 일은 그대로 쓴다. */
function TodaySchedule({ centerId, userId, role }: { centerId: string; userId: string; role: string }) {
  const today = todayKst();
  const q = useLoad(async () => {
    const [{ sessions }, { clients }] = await Promise.all([api.sessions(centerId, thisMonthKst()), api.clients(centerId)]);
    const list = sessions
      .filter((s) => s.startAt.slice(0, 10) === today && s.status !== 'CANCELLED' && (role !== 'THERAPIST' || s.therapistId === userId))
      .sort((a, b) => a.startAt.localeCompare(b.startAt));
    return list.map((s) => ({ id: s.id, at: time(s.startAt), label: `${clients.find((c) => c.id === s.clientId)?.displayName ?? ''} ${s.program ?? ''}`.trim() }));
  }, [centerId, today]);
  if (!q.data?.length) return null;
  return (
    <aside className="todo-schedule" aria-label="오늘 수업">
      <ul>{q.data.slice(0, 5).map((s) => <li key={s.id}><span>{s.at}</span> {s.label}</li>)}</ul>
      {q.data.length > 5 && <p className="hint">외 {q.data.length - 5}건</p>}
    </aside>
  );
}

export function Todos() {
  const { me } = useAuth();
  const m = me!;
  const p = principalOf(m);
  const today = todayKst();
  const q = useLoad(() => api.todos(), []);
  const canDelegate = Boolean(m.centerId) && can(p, 'todo:delegate');
  const staff = useLoad(() => (canDelegate ? api.staff(m.centerId!) : Promise.resolve({ staff: [] as StaffRow[] })), [canDelegate, m.centerId]);
  const teachers = (staff.data?.staff ?? []).filter((s) => s.active && s.role === 'THERAPIST');
  const clientList = useLoad(() => (canDelegate ? api.clients(m.centerId!) : Promise.resolve({ clients: [] as ClientRow[] })), [canDelegate, m.centerId]);
  const [place, setPlace] = useState<Place>({ kind: 'view', view: 'TODAY' });
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [clarifying, setClarifying] = useState(false);
  const [newProject, setNewProject] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captureRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const placeRef = useRef(place);
  placeRef.current = place;

  const data: TodoList | null = q.data;
  const todos = useMemo(() => data?.todos ?? [], [data]);
  const projects = useMemo(() => data?.projects ?? [], [data]);
  const counts = useMemo(() => todoCounts(todos, projects, today), [todos, projects, today]);
  const review = useMemo(() => weeklyReview(todos, projects, today, data?.lastReviewedAt ?? undefined), [todos, projects, today, data?.lastReviewedAt]);
  const openTodo = todos.find((t) => t.id === openId) ?? null;
  const project = place.kind === 'project' ? projects.find((x) => x.id === place.id) : undefined;
  const inbox = useMemo(() => todoView('INBOX', todos, projects, today), [todos, projects, today]);

  const run: Run = async (fn, onError = setError) => {
    setError(null);
    try { await fn(); } catch (e) { onError(errorText(e)); }
    q.reload();
  };
  const go = (next: Place) => { setPlace(next); setTag(null); setQuery(''); };

  // 단축키: N 새 할 일, / 찾기, 1~7 목록. 입력 중이거나 대화상자가 열려 있으면 쓰지 않는다.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.metaKey || e.ctrlKey || e.altKey || document.querySelector('dialog[open]') || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); captureRef.current?.focus(); }
      else if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
      else if (/^[1-7]$/.test(e.key)) go({ kind: 'view', view: (['INBOX', 'TODAY', 'UPCOMING', 'NEXT', 'WAITING', 'SOMEDAY', 'LOGBOOK'] as const)[Number(e.key) - 1]! });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const capture = (e: FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) return;
    setTitle('');
    const cur = placeRef.current;
    void run(() => api.createTodo(cur.kind === 'project' ? { title: t, projectId: cur.id } : cur.kind === 'view' && cur.view === 'TODAY' ? { title: t, startDate: today } : { title: t }));
  };
  const captureHint = place.kind === 'project' ? '이 프로젝트에 추가' : place.kind === 'view' && place.view === 'TODAY' ? '오늘 할 일로 추가' : '수신함에 넣기';

  const rest = { projects, today, run, onOpen: (t: Todo) => setOpenId(t.id) };
  const list = place.kind === 'view' ? todoView(place.view, todos, projects, today) : [];
  const filtered = tag ? list.filter((t) => t.tags.includes(tag)) : list;
  const found = query.trim() ? searchTodos(todos, query) : null;
  const areas = useMemo(() => {
    const open = projects.filter((x) => x.status === 'OPEN');
    return [...new Set(open.map((x) => x.area))].sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b, 'ko'))).map((area) => ({ area, items: open.filter((x) => x.area === area) }));
  }, [projects]);
  const isView = (v: TodoView) => place.kind === 'view' && place.view === v;
  const onNavKey = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Escape') setQuery(''); };

  const body = (
    <div className="todos">
      <nav className="todo-side" aria-label="할 일 목록">
        <input ref={searchRef} className="todo-search" type="search" aria-label="할 일 찾기" placeholder="찾기" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onNavKey} />
        <ul>
          {(['INBOX', 'TODAY', 'UPCOMING', 'NEXT', 'WAITING', 'SOMEDAY', 'LOGBOOK'] as const).map((v) => (
            <li key={v} className={v === 'LOGBOOK' || v === 'INBOX' ? 'todo-side-gap' : undefined}>
              <button type="button" aria-current={isView(v) ? 'page' : undefined} onClick={() => go({ kind: 'view', view: v })}>
                <span>{VIEW_LABEL[v]}</span>{counts[v] > 0 && v !== 'LOGBOOK' && <span className="todo-count">{counts[v]}</span>}
              </button>
            </li>
          ))}
          <li>
            <button type="button" aria-current={place.kind === 'review' ? 'page' : undefined} onClick={() => go({ kind: 'review' })}>
              <span>주간 검토</span>{review.due && <span className="todo-count due">할 때</span>}
            </button>
          </li>
        </ul>
        {areas.map(({ area, items }) => (
          <div key={area || 'none'} className="todo-area">
            <h3>{area || '프로젝트'}</h3>
            <ul>
              {items.map((x) => (
                <li key={x.id}>
                  <button type="button" aria-current={place.kind === 'project' && place.id === x.id ? 'page' : undefined} onClick={() => go({ kind: 'project', id: x.id })}>
                    <Ring ratio={projectProgress(x, todos).ratio} /><span>{x.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <button type="button" className="btn btn-ghost btn-sm todo-new-project" onClick={() => setNewProject(true)}>+ 새 프로젝트</button>
        <p className="hint todo-keys">단축키: N 새 할 일 · / 찾기 · 1~7 목록</p>
      </nav>
      <div className="todo-main">
        <form className="todo-capture" onSubmit={capture}>
          <input ref={captureRef} type="text" aria-label="떠오른 일 적기" maxLength={200} placeholder={`떠오른 일 적기 · ${captureHint}`} value={title} onChange={(e) => setTitle(e.target.value)} />
          <button type="submit" className="btn btn-primary">추가</button>
        </form>
        <p className="hint">{m.role === 'CENTER_DIRECTOR' || m.role === 'THERAPIST' ? '아동 이름·연락처·진단명은 되도록 적지 마세요. 필요한 기록은 이용자·회기 기록에 남기세요.' : '다른 사람의 개인정보는 되도록 적지 마세요.'}</p>
        {error && <Alert>{error}</Alert>}
        {q.error && <Alert>{q.error}</Alert>}
        {q.loading && !data && <Loading />}
        {data && (found ? (
          <section className="stack" aria-label="찾은 할 일">
            <h2>“{query.trim()}” 찾은 결과 {found.length}개</h2>
            {found.length ? <RowList todos={found} {...rest} /> : <Empty>찾는 할 일이 없습니다.</Empty>}
          </section>
        ) : place.kind === 'review' ? (
          <ReviewPage data={data} today={today} run={run} onOpen={rest.onOpen} onClarify={() => setClarifying(true)} onProject={(id) => go({ kind: 'project', id })} />
        ) : place.kind === 'project' ? (
          project ? <ProjectPage key={project.id} project={project} todos={todos} projects={projects} today={today} run={run} onOpen={rest.onOpen} onLeave={() => go({ kind: 'view', view: 'TODAY' })} /> : <Empty>프로젝트를 찾을 수 없습니다.</Empty>
        ) : (
          <section className="stack" aria-labelledby="todo-view-title">
            <div className="todo-view-head">
              <div><h2 id="todo-view-title">{VIEW_LABEL[place.view]}</h2><p className="muted small">{VIEW_HELP[place.view]}</p></div>
              {place.view === 'INBOX' && inbox.length > 0 && <button type="button" className="btn btn-primary" onClick={() => setClarifying(true)}>하나씩 정리하기</button>}
            </div>
            {place.view === 'TODAY' && m.centerId && can(p, 'session:read') && <TodaySchedule centerId={m.centerId} userId={m.userId} role={m.role} />}
            <TagFilter tags={todoTags(list)} value={tag} onChange={setTag} />
            {list.length === 0 && <Empty>{VIEW_EMPTY[place.view]}</Empty>}
            {place.view === 'TODAY' ? <>
              <RowList todos={filtered.filter((t) => !t.evening)} {...rest} />
              {filtered.some((t) => t.evening) && <div className="todo-section"><h3>오늘 저녁</h3><RowList todos={filtered.filter((t) => t.evening)} {...rest} /></div>}
            </> : <RowList todos={filtered} {...rest} />}
            {place.view === 'LOGBOOK' && (data.olderDone ?? 0) > 0 && <p className="hint">오래된 기록 {data.olderDone}개는 보이지 않습니다. 최근 것부터 보여 줍니다.</p>}
            {(place.view === 'SOMEDAY' || place.view === 'LOGBOOK') && (() => {
              const shown = projects.filter((x) => (place.view === 'SOMEDAY' ? x.status === 'SOMEDAY' : x.status === 'DONE' || x.status === 'CANCELED'));
              return shown.length > 0 && <div className="todo-section"><h3>프로젝트</h3><ul className="plain">{shown.map((x) => <li key={x.id}><button type="button" className="btn btn-ghost btn-sm" onClick={() => go({ kind: 'project', id: x.id })}>{x.title}</button></li>)}</ul></div>;
            })()}
          </section>
        ))}
      </div>
      <TodoEditor todo={openTodo} projects={projects} tags={todoTags(todos)} today={today} teachers={teachers} clients={clientList.data?.clients ?? []} canDelegate={canDelegate} run={run} onClose={() => setOpenId(null)} />
      <Clarify open={clarifying} onClose={() => setClarifying(false)} inbox={inbox} projects={projects} teachers={teachers} canDelegate={canDelegate} today={today} run={run} />
      <ProjectDialog project={null} open={newProject} onClose={() => setNewProject(false)} run={run} onCreated={(id) => go({ kind: 'project', id })} />
    </div>
  );
  // 학생 계정은 위쪽 메뉴 틀을 쓴다(.student 가 있으면 왼쪽 메뉴 대신 위쪽 메뉴가 된다)
  return m.role === 'STUDENT' ? <div className="student todos-student"><h1>할 일 관리</h1>{body}</div> : <><PageHeader title="할 일 관리" sub="떠오른 일을 모으고, 정리하고, 하나씩 끝냅니다. 내 할 일은 나만 볼 수 있습니다." />{body}</>;
}
